import type { FormEvent } from 'react'
import { useRef, useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { AtSign, Check, Lock, ShieldCheck, User } from 'lucide-react'
import { MIN_PASSWORD_LENGTH } from '@shared/constants'
import AuthLayout, { AuthNotice, fieldErrorsFrom } from '@/components/auth/AuthLayout'
import PasswordField, { passwordStrength } from '@/components/auth/PasswordField'
import {
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Input
} from '@/components/ui'
import { isApiError } from '@/lib/api'
import { useAuthStore } from '@/stores/useAuthStore'

const WHAT_HAPPENS = [
  'This account becomes the instance administrator.',
  'You can invite everyone else from the Admin page afterwards.',
  'First-run setup closes for good once the account exists.'
]

export default function SetupPage(): React.ReactElement {
  const navigate = useNavigate()
  const session = useAuthStore((state) => state.session)
  const user = useAuthStore((state) => state.user)
  const bootstrap = useAuthStore((state) => state.bootstrap)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [conflict, setConflict] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // A 409 means users appeared behind our back, so the cached session — which
  // still claims `needsBootstrap` — must not be trusted to redirect us.
  const conflicted = useRef(false)

  const mismatch = confirm.length > 0 && confirm !== password

  if (session && !session.needsBootstrap && !conflicted.current) {
    return <Navigate to={user ? '/sites' : '/login'} replace />
  }

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {}

    if (!name.trim()) errors.name = 'Enter your name.'
    if (!email.trim()) errors.email = 'Enter your email address.'
    if (!passwordStrength(password).meetsMinimum) {
      errors.password = `Use at least ${MIN_PASSWORD_LENGTH} characters.`
    }
    if (confirm !== password) errors.confirm = 'Passwords do not match.'

    return errors
  }

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    if (submitting) return

    setFormError(null)
    const invalid = validate()
    setFieldErrors(invalid)
    if (Object.keys(invalid).length > 0) return

    setSubmitting(true)
    try {
      await bootstrap(email.trim(), name.trim(), password)
      // Leaves `submitting` set: this component unmounts with the redirect.
      navigate('/sites', { replace: true })
    } catch (error) {
      if (isApiError(error) && error.status === 409) {
        conflicted.current = true
        setConflict(error.message)
      } else if (isApiError(error)) {
        const details = fieldErrorsFrom(error.details)
        setFieldErrors(details)
        setFormError(Object.keys(details).length > 0 ? null : error.message)
      } else {
        setFormError('Something went wrong. Please try again.')
      }
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout tagline="First-run setup — nobody has an account on this instance yet.">
      <Card>
        <CardHeader>
          <CardTitle>Create the first administrator</CardTitle>
          <CardDescription>
            Argus has no users yet. The account you create here gets full access to instance
            settings, SSH keys and user management.
          </CardDescription>
        </CardHeader>

        <CardBody>
          {conflict ? (
            <div className="space-y-4">
              <AuthNotice title="Setup is already complete">
                <p>{conflict}</p>
              </AuthNotice>
              <p className="text-sm leading-relaxed text-fg-muted">
                This instance already has at least one account, so first-run setup is closed.{' '}
                <Link
                  to="/login"
                  replace
                  className="font-semibold text-accent underline decoration-transparent underline-offset-2 transition-colors hover:decoration-current"
                >
                  Go to sign in
                </Link>
                .
              </p>
            </div>
          ) : (
            <form
              noValidate
              className="space-y-4"
              onSubmit={(event) => {
                void submit(event)
              }}
            >
              {formError ? (
                <AuthNotice>
                  <p>{formError}</p>
                </AuthNotice>
              ) : null}

              <ul className="space-y-1.5 rounded-control border border-line bg-sunken px-3 py-2.5">
                {WHAT_HAPPENS.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2 text-xs leading-relaxed text-fg-muted"
                  >
                    <Check className="mt-0.5 size-3.5 shrink-0 text-accent" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <Input
                label="Name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="name"
                autoFocus
                leadingIcon={<User />}
                error={fieldErrors.name}
                disabled={submitting}
                required
              />

              <Input
                label="Email"
                type="email"
                inputMode="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                leadingIcon={<AtSign />}
                hint="You will sign in with this address."
                error={fieldErrors.email}
                disabled={submitting}
                required
              />

              <PasswordField
                label="Password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                leadingIcon={<Lock />}
                showStrength
                error={fieldErrors.password}
                disabled={submitting}
                required
              />

              <PasswordField
                label="Confirm password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
                leadingIcon={<Lock />}
                error={mismatch ? 'Passwords do not match.' : fieldErrors.confirm}
                disabled={submitting}
                required
              />

              <Button
                type="submit"
                size="lg"
                fullWidth
                loading={submitting}
                iconLeft={<ShieldCheck />}
              >
                Create administrator
              </Button>
            </form>
          )}
        </CardBody>
      </Card>
    </AuthLayout>
  )
}

export { SetupPage }
